/**
 * @fileoverview Real-time WebSocket communication client for the 'Rasca y gana' (Scratcher) Fan mobile dynamic (Tile ID 43).
 * Manages WebSocket connection lifecycle, automatic reconnection, character/slot registration,
 * real-time scratch progress broadcasting, and state synchronization from the fan's mobile view (/scratcher).
 * 
 * @module utilities/WS/ScratcherFanWS
 */

let scratcherFanWebSocket = null;
let isScratcherFanConnected = false;
let reconnectionTimeoutIdentifier = null;
let reconnectionAttemptCounter = 0;
let lastRegisteredPlayerId = null;
let lastRegisteredNickname = null;

const MAXIMUM_RECONNECTION_ATTEMPTS = 20;
const RECONNECTION_DELAY_MILLISECONDS = 2500;

/**
 * Establishes a persistent WebSocket connection for the Scratcher fan mobile experience.
 * 
 * @function ConnectWebSocketForScratcherFan
 * @param {Function} [onMessageReceived=()=>{}] - Callback invoked whenever a parsed JSON message is received.
 * @param {Function} [onStatusChange=()=>{}] - Callback invoked when the connection status changes ('connecting', 'connected', 'reconnecting', 'error', 'disconnected').
 * @param {string} [initialPlayerId=null] - Optional player ID to automatically register upon connection.
 * @param {string} [initialNickname=null] - Optional nickname to register upon connection.
 */
export const ConnectWebSocketForScratcherFan = (
    onMessageReceived = () => {},
    onStatusChange = () => {},
    initialPlayerId = null,
    initialNickname = null
) => {
    if (initialPlayerId) {
        lastRegisteredPlayerId = initialPlayerId;
    }
    if (initialNickname) {
        lastRegisteredNickname = initialNickname;
    }

    if (scratcherFanWebSocket || isScratcherFanConnected) {
        if (lastRegisteredPlayerId && scratcherFanWebSocket?.readyState === WebSocket.OPEN) {
            SendScratcherJoinSlot(lastRegisteredPlayerId, lastRegisteredNickname);
        }
        return;
    }

    const establishConnection = () => {
        if (reconnectionAttemptCounter >= MAXIMUM_RECONNECTION_ATTEMPTS) {
            onStatusChange('error');
            return;
        }

        reconnectionAttemptCounter += 1;
        onStatusChange('connecting');

        try {
            const webSocketEndpointUrl = `${process.env.REACT_APP_API_URL_WSS}?tipo=overlay:scratcher:fan`;
            scratcherFanWebSocket = new WebSocket(webSocketEndpointUrl);

            scratcherFanWebSocket.onopen = () => {
                isScratcherFanConnected = true;
                reconnectionAttemptCounter = 0;
                onStatusChange('connected');

                // If player was already chosen, register slot immediately
                if (lastRegisteredPlayerId) {
                    SendScratcherJoinSlot(lastRegisteredPlayerId, lastRegisteredNickname);
                }

                // Request initial snapshot of the game state
                RequestScratcherFanState();
            };

            scratcherFanWebSocket.onclose = () => {
                isScratcherFanConnected = false;
                scratcherFanWebSocket = null;
                onStatusChange('reconnecting');

                clearTimeout(reconnectionTimeoutIdentifier);
                reconnectionTimeoutIdentifier = setTimeout(establishConnection, RECONNECTION_DELAY_MILLISECONDS);
            };

            scratcherFanWebSocket.onerror = () => {
                onStatusChange('error');
            };

            scratcherFanWebSocket.onmessage = (messageEvent) => {
                try {
                    const parsedPayload = JSON.parse(messageEvent.data);
                    onMessageReceived(parsedPayload);
                } catch (parseError) {
                    // Ignore non-JSON or malformed packets
                }
            };
        } catch (connectionError) {
            onStatusChange('error');
            clearTimeout(reconnectionTimeoutIdentifier);
            reconnectionTimeoutIdentifier = setTimeout(establishConnection, RECONNECTION_DELAY_MILLISECONDS);
        }
    };

    establishConnection();
};

/**
 * Sends a JSON payload through the active Scratcher fan WebSocket connection.
 * 
 * @function SendScratcherFanMessage
 * @param {Object} payloadObject - JSON object to transmit.
 * @returns {boolean} True if successfully sent, false otherwise.
 */
export const SendScratcherFanMessage = (payloadObject) => {
    try {
        if (
            scratcherFanWebSocket &&
            scratcherFanWebSocket.readyState === WebSocket.OPEN
        ) {
            scratcherFanWebSocket.send(JSON.stringify(payloadObject));
            return true;
        }
        return false;
    } catch (dispatchError) {
        return false;
    }
};

/**
 * Registers the chosen character/slot for this fan connection.
 * 
 * @function SendScratcherJoinSlot
 * @param {string} playerId - Player identifier ('player1', 'player2', 'player3', etc.).
 * @param {string} [nickname=''] - Optional participant nickname.
 * @returns {boolean} True if sent successfully, false otherwise.
 */
export const SendScratcherJoinSlot = (playerId, nickname = '') => {
    if (!playerId) {
        return false;
    }

    lastRegisteredPlayerId = playerId;
    lastRegisteredNickname = nickname;

    return SendScratcherFanMessage({
        type: 'scratcher_join_slot',
        playerId: playerId,
        nickname: nickname || '',
        data: {
            playerId: playerId,
            nickname: nickname || ''
        }
    });
};

/**
 * Dispatches real-time scratching progress to the server.
 * 
 * @function SendScratcherProgress
 * @param {string} playerId - Active player identifier.
 * @param {number} scratchedPercentage - Calculated progress percentage (0 - 100).
 * @param {number} [totalStrokes=1] - Number of scratch stroke iterations performed.
 * @returns {boolean} True if sent successfully, false otherwise.
 */
export const SendScratcherProgress = (playerId, scratchedPercentage, totalStrokes = 1) => {
    if (!playerId) {
        return false;
    }

    const sanitizedPercentage = Math.min(100, Math.max(0, Number(scratchedPercentage) || 0));
    const sanitizedStrokes = Math.max(1, Number(totalStrokes) || 1);

    return SendScratcherFanMessage({
        type: 'scratcher_progress',
        playerId: playerId,
        percentage: sanitizedPercentage,
        strokes: sanitizedStrokes,
        data: {
            playerId: playerId,
            percentage: sanitizedPercentage,
            strokes: sanitizedStrokes
        }
    });
};

/**
 * Requests the current game state snapshot from the server.
 * 
 * @function RequestScratcherFanState
 * @returns {boolean} True if sent successfully, false otherwise.
 */
export const RequestScratcherFanState = () => {
    return SendScratcherFanMessage({
        type: 'scratcher_get_state'
    });
};

/**
 * Cleanly disconnects and terminates the Scratcher fan WebSocket connection.
 * 
 * @function DisconnectWebSocketForScratcherFan
 */
export const DisconnectWebSocketForScratcherFan = () => {
    try {
        if (reconnectionTimeoutIdentifier) {
            clearTimeout(reconnectionTimeoutIdentifier);
            reconnectionTimeoutIdentifier = null;
        }

        if (scratcherFanWebSocket) {
            scratcherFanWebSocket.onopen = null;
            scratcherFanWebSocket.onclose = null;
            scratcherFanWebSocket.onerror = null;
            scratcherFanWebSocket.onmessage = null;

            if (scratcherFanWebSocket.readyState === WebSocket.OPEN) {
                scratcherFanWebSocket.close();
            } else if (scratcherFanWebSocket.readyState === WebSocket.CONNECTING) {
                const socketToClose = scratcherFanWebSocket;
                socketToClose.onopen = () => {
                    try {
                        socketToClose.close();
                    } catch {
                        // Safe ignore
                    }
                };
            }

            scratcherFanWebSocket = null;
            isScratcherFanConnected = false;
            reconnectionAttemptCounter = 0;
        }
    } catch (teardownError) {
        // Safe teardown
    }
};
